/**
 * pvl-privacy-focus.js
 *
 * QUÉ HACE
 * Contiene el foco de teclado dentro del componente de privacidad de Shopify
 * (`#shopify-pc__banner` y `#shopify-pc__prefs`) mientras está visible, y devuelve
 * el foco a un destino lógico cuando se cierra.
 *
 * POR QUÉ (issue #114)
 * El diálogo de consentimiento inicial de Shopify se anuncia como `alertdialog`
 * pero deja escapar el foco hacia el contenido de fondo con Tab / Shift+Tab, y el
 * control "Gestionar preferencias" no tenía indicador de foco perceptible. El
 * indicador visible se resuelve en el CSS crítico de `layout/theme.liquid`; este
 * archivo resuelve únicamente la gestión del foco.
 *
 * QUÉ NO HACE (deliberadamente)
 * No intercepta clics, no aplica `inert`, no añade `aria-modal`, no mueve ni
 * elimina ningún nodo de Shopify y no toca `window.Shopify.customerPrivacy`.
 * La selección y persistencia del consentimiento siguen siendo responsabilidad
 * exclusiva de los manejadores propios de Shopify.
 *
 * NOTA DE DETECCIÓN
 * El tema oculta el banner con `visibility: hidden` hasta añadir `.show-banner`
 * (ver el bloque "CRITICAL CSS" de `layout/theme.liquid`). Como `visibility` se
 * hereda, el filtro de visibilidad devuelve cero elementos enfocables mientras el
 * banner está oculto: eso basta para saber si el diálogo está abierto, sin
 * depender de IDs internos e inestables de los botones de Shopify.
 */
(function () {
  'use strict';

  if (window.__pvlPrivacyFocus) return;
  window.__pvlPrivacyFocus = true;

  var ROOT_SELECTORS = ['#shopify-pc__banner', '#shopify-pc__prefs'];
  var FALLBACK_SELECTOR = '#MainContent';

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    '[contenteditable="true"]',
    '[tabindex]'
  ].join(',');

  // Margen para absorber el traspaso banner -> preferencias: al pulsar
  // "Gestionar preferencias" el banner se oculta y el modal aparece, a veces en
  // fotogramas distintos. Si restauráramos el foco de inmediato, el foco saltaría
  // al fondo entre ambos estados.
  var CLOSE_GRACE_MS = 60;

  var returnTarget = null;
  var activeRoot = null;
  var wasOpen = false;
  var isMovingFocus = false;
  var lastInteractionWasPointer = false;
  var evaluateScheduled = false;
  var closeTimer = null;

  function isInDocument(el) {
    return !!el && document.documentElement.contains(el);
  }

  function isVisible(el) {
    if (!isInDocument(el)) return false;
    if (el.hasAttribute('hidden')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    if (!el.getClientRects().length) return false;

    var style = window.getComputedStyle(el);
    if (!style) return false;
    if (style.display === 'none') return false;
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;

    return true;
  }

  function observeRoot(el) {
    if (el.__pvlPrivacyFocusObserved) return;
    el.__pvlPrivacyFocusObserved = true;

    if (typeof window.MutationObserver !== 'function') return;

    try {
      var observer = new MutationObserver(scheduleEvaluate);
      observer.observe(el, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
      });
    } catch (error) {
      /* Sin observador de atributos seguimos funcionando con los reintentos. */
    }
  }

  function collectOpenRoots() {
    var openRoots = [];

    for (var i = 0; i < ROOT_SELECTORS.length; i++) {
      var root = document.querySelector(ROOT_SELECTORS[i]);
      if (!root) continue;
      observeRoot(root);
      if (isVisible(root)) openRoots.push(root);
    }

    return openRoots;
  }

  function getFocusables(root) {
    var candidates = root.querySelectorAll(FOCUSABLE_SELECTOR);
    var focusables = [];

    for (var i = 0; i < candidates.length; i++) {
      // Cualquier tabindex negativo (-1, -2, ...) queda fuera de la navegacion
      // secuencial segun el estandar, asi que no entra en el ciclo del trap.
      // Comparar el numero tambien excluye casos como <button tabindex="-1">.
      if (candidates[i].tabIndex < 0) continue;
      if (isVisible(candidates[i])) focusables.push(candidates[i]);
    }

    return focusables;
  }

  function moveFocus(el) {
    if (!el) return;
    isMovingFocus = true;
    try {
      el.focus({ preventScroll: true });
    } catch (error) {
      try {
        el.focus();
      } catch (innerError) {
        /* Elemento no enfocable: no hay nada que hacer. */
      }
    }
    isMovingFocus = false;
  }

  function focusRoot(root) {
    if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
    moveFocus(root);
  }

  function containsFocus(root, node) {
    return root === node || root.contains(node);
  }

  function findOwningRoot(openRoots, node) {
    if (!node) return null;
    for (var i = 0; i < openRoots.length; i++) {
      if (containsFocus(openRoots[i], node)) return openRoots[i];
    }
    return null;
  }

  // El último selector abierto es el más "encima" (preferencias sobre banner).
  function topRoot(openRoots) {
    return openRoots[openRoots.length - 1];
  }

  function captureReturnTarget() {
    // Solo capturamos el destino de retorno en la transición real de "ninguno
    // abierto" a "alguno abierto". Si ya hay uno guardado estamos en pleno
    // traspaso banner -> preferencias y debe sobrevivir intacto.
    if (returnTarget !== null) return;

    var active = document.activeElement;
    if (active && active !== document.body && isInDocument(active)) {
      returnTarget = active;
    }
  }

  function canReceiveFocus(el) {
    if (!isInDocument(el)) return false;
    if (el.disabled) return false;
    return isVisible(el);
  }

  function restoreFocus() {
    var target = canReceiveFocus(returnTarget) ? returnTarget : document.querySelector(FALLBACK_SELECTOR);
    returnTarget = null;
    if (target) moveFocus(target);
  }

  function handleClose() {
    if (closeTimer !== null) return;

    closeTimer = window.setTimeout(function () {
      closeTimer = null;
      // Si algo se reabrió durante la gracia, conservamos el destino de retorno.
      if (collectOpenRoots().length) return;
      restoreFocus();
    }, CLOSE_GRACE_MS);
  }

  function evaluate() {
    var openRoots = collectOpenRoots();

    if (!openRoots.length) {
      if (wasOpen) {
        wasOpen = false;
        activeRoot = null;
        handleClose();
      }
      return;
    }

    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    if (!wasOpen) {
      wasOpen = true;
      captureReturnTarget();
    }

    // El foco se mueve cada vez que cambia el diálogo superior, no solo al
    // abrir el primero: eso cubre el traspaso banner -> preferencias, donde
    // ambas raíces pueden solaparse durante uno o dos fotogramas. Si Shopify
    // ya colocó el foco dentro del diálogo superior, no se lo quitamos.
    var top = topRoot(openRoots);
    if (top !== activeRoot) {
      activeRoot = top;
      if (!containsFocus(top, document.activeElement)) focusRoot(top);
    }
  }

  function scheduleEvaluate() {
    if (evaluateScheduled) return;
    evaluateScheduled = true;

    var run = function () {
      evaluateScheduled = false;
      evaluate();
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run);
    } else {
      window.setTimeout(run, 16);
    }
  }

  function onKeydown(event) {
    lastInteractionWasPointer = false;

    var isTab = event.key === 'Tab' || event.keyCode === 9;
    if (!isTab) return;

    var openRoots = collectOpenRoots();
    if (!openRoots.length) return;

    // El trap siempre opera sobre el diálogo superior. Si el foco quedó en una
    // raíz inferior (banner que aún no terminó de ocultarse bajo el modal de
    // preferencias), se trata como si estuviera fuera y se trae al de encima.
    var active = document.activeElement;
    var root = topRoot(openRoots);
    var isOutside = findOwningRoot(openRoots, active) !== root;

    var focusables = getFocusables(root);

    if (!focusables.length) {
      event.preventDefault();
      focusRoot(root);
      return;
    }

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (isOutside || active === root) {
      event.preventDefault();
      moveFocus(event.shiftKey ? last : first);
      return;
    }

    var index = focusables.indexOf(active);

    if (event.shiftKey) {
      if (index <= 0) {
        event.preventDefault();
        moveFocus(last);
      }
      return;
    }

    if (index === -1 || index === focusables.length - 1) {
      event.preventDefault();
      moveFocus(first);
    }
  }

  function onFocusin(event) {
    if (isMovingFocus) return;

    var openRoots = collectOpenRoots();
    if (!openRoots.length) return;
    if (findOwningRoot(openRoots, event.target)) return;

    // Con ratón o táctil no secuestramos el foco: el usuario apuntó
    // explícitamente a otro sitio y robárselo rompería campos de formulario.
    // La contención por teclado ya la garantiza `onKeydown`.
    if (lastInteractionWasPointer) return;

    focusRoot(topRoot(openRoots));
  }

  function onPointerInteraction() {
    lastInteractionWasPointer = true;
  }

  function startBodyObserver() {
    if (!document.body) return;
    if (typeof window.MutationObserver !== 'function') return;

    try {
      var observer = new MutationObserver(scheduleEvaluate);
      observer.observe(document.body, { childList: true });
    } catch (error) {
      /* Sin observador nos apoyamos en los reintentos posteriores a load. */
    }
  }

  function init() {
    startBodyObserver();
    evaluate();
  }

  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('focusin', onFocusin, true);
  document.addEventListener('pointerdown', onPointerInteraction, true);
  document.addEventListener('mousedown', onPointerInteraction, true);
  // En navegadores sin soporte de opciones, el objeto se evalúa como `useCapture`
  // verdadero, de modo que el comportamiento de captura se mantiene igual.
  document.addEventListener('touchstart', onPointerInteraction, { capture: true, passive: true });

  if (document.body) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // El tema revela el banner en `load` + 100 ms. Reevaluamos por si la raíz no
  // existía todavía cuando se montó el observador de `body`.
  window.addEventListener('load', function () {
    scheduleEvaluate();
    window.setTimeout(scheduleEvaluate, 250);
    window.setTimeout(scheduleEvaluate, 1000);
  });
})();
